import { lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react'

import { reloadingOnNewRelease } from '@/shared/layout/new-release'

import type {
  ActionContribution,
  AdminHomeCategory,
  AdminHomeLink,
  AdminHomeSection,
  ColumnCellProps,
  ColumnContribution,
  ComponentLoader,
  Feature,
  FeatureRoute,
  LinkContribution,
  NavItem,
  PanelContribution,
  Provider,
  SlotContext,
  SlotContribution,
  TabContribution,
  TabCountProps,
} from './types'

type Lazy<P> = LazyExoticComponent<ComponentType<P>>

export interface RegisteredRoute extends FeatureRoute {
  readonly featureId: string
  readonly Page: Lazy<object>
}

export type RegisteredTab = TabContribution & {
  readonly Component: Lazy<SlotContext>
  readonly Count?: Lazy<TabCountProps>
}
export type RegisteredPanel = PanelContribution & { readonly Component: Lazy<SlotContext> }
export type RegisteredAction = ActionContribution & { readonly Component: Lazy<SlotContext> }
export type RegisteredLink = LinkContribution
export type RegisteredColumn = ColumnContribution & {
  readonly Cell: Lazy<ColumnCellProps<unknown>>
}

export type RegisteredContribution =
  RegisteredTab | RegisteredPanel | RegisteredAction | RegisteredLink | RegisteredColumn

/** The features, flattened and checked once at startup. Order is registry order throughout. */
export interface Registry {
  readonly routes: readonly RegisteredRoute[]
  readonly nav: readonly NavItem[]
  readonly adminHome: {
    readonly sections: readonly AdminHomeSection[]
    readonly categories: readonly AdminHomeCategory[]
    readonly links: readonly AdminHomeLink[]
    /** The registered layout hook's wrapper, or one that adds nothing. */
    readonly Layout: Provider | Lazy<{ readonly children: ReactNode }>
  }
  readonly slots: ReadonlyMap<string, readonly RegisteredContribution[]>
  /** The features' app-wide providers, outermost first. */
  readonly providers: readonly Provider[]
}

/** Until a feature fills Admin Home's hook, the page keeps its registry order. */
function RegistryOrder(props: { readonly children: ReactNode }) {
  return props.children
}

/** A column with its value type checked, for a feature's `slots` list. */
export function column<T>(contribution: ColumnContribution<T>): SlotContribution {
  // The cell and the loader agree on `T`; the registry stores every column alike.
  return contribution as unknown as ColumnContribution
}

function lazyFrom<P>(load: ComponentLoader<P>): Lazy<P> {
  const loadOrReload = reloadingOnNewRelease(load)
  return lazy(async () => ({ default: await loadOrReload() }))
}

function register(contribution: SlotContribution): RegisteredContribution {
  switch (contribution.kind) {
    case 'tab':
      return {
        ...contribution,
        Component: lazyFrom(contribution.component),
        ...(contribution.count === undefined ? {} : { Count: lazyFrom(contribution.count) }),
      }
    case 'panel':
    case 'action':
      return { ...contribution, Component: lazyFrom(contribution.component) }
    case 'column':
      return { ...contribution, Cell: lazyFrom(contribution.cell) }
    case 'link':
      return contribution
  }
}

/**
 * A route path as the router matches it under the shell's pathless layout: with a leading slash
 * and without case, a trailing slash or parameter names, so `reports/:id` and
 * `/Reports/:publicId/` are the same route. An optional `?` is kept.
 */
function routeKey(path: string): string {
  const absolute = path.startsWith('/') ? path : `/${path}`
  return (
    absolute
      .toLowerCase()
      .replace(/\/:[\w-]+/g, '/:')
      .replace(/\/+$/, '') || '/'
  )
}

/** Throws on a second use of a key, naming what clashed, whether two features or one. */
function claim(seen: Set<string>, what: string, key: string): void {
  if (seen.has(key)) {
    throw new Error(`The ${what} "${key}" is registered twice.`)
  }
  seen.add(key)
}

/**
 * Flattens the features into one registry, and fails fast on anything two features both claim,
 * a route the shell keeps for itself (`shellPaths`, such as the sign-in callback), or anything
 * that names what nobody registers, so a mistake shows the first time the app or a test starts.
 * Each page and contribution is wrapped in `lazy` here, once.
 */
export function buildRegistry(
  features: readonly Feature[],
  shellPaths: readonly string[] = [],
): Registry {
  const featureIds = new Set<string>()
  const routes: RegisteredRoute[] = []
  const paths = new Map<string, string>()
  const shellKeys = new Set(shellPaths.map(routeKey))
  const nav: NavItem[] = []
  const navIds = new Set<string>()
  const sections: AdminHomeSection[] = []
  const categories: AdminHomeCategory[] = []
  const links: AdminHomeLink[] = []
  const adminIds = {
    section: new Set<string>(),
    category: new Set<string>(),
    link: new Set<string>(),
  }
  let layout: Registry['adminHome']['Layout'] | undefined
  const slots = new Map<string, RegisteredContribution[]>()
  const providers: Provider[] = []

  for (const feature of features) {
    claim(featureIds, 'feature', feature.id)

    for (const route of feature.routes ?? []) {
      if (shellKeys.has(routeKey(route.path))) {
        throw new Error(`The route "${route.path}" is one the shell keeps for itself.`)
      }
      const first = paths.get(routeKey(route.path))
      if (first !== undefined) {
        throw new Error(`The route "${route.path}" matches the same addresses as "${first}".`)
      }
      paths.set(routeKey(route.path), route.path)
      routes.push({ ...route, featureId: feature.id, Page: lazyFrom(route.page) })
    }

    for (const item of feature.nav ?? []) {
      claim(navIds, 'nav item', item.id)
      nav.push(item)
    }

    const adminHome = feature.adminHome ?? {}
    for (const section of adminHome.sections ?? []) {
      claim(adminIds.section, 'Admin Home section', section.id)
      sections.push(section)
    }
    for (const category of adminHome.categories ?? []) {
      claim(adminIds.category, 'Admin Home category', category.id)
      categories.push(category)
    }
    for (const link of adminHome.links ?? []) {
      claim(adminIds.link, 'Admin Home link', link.id)
      links.push(link)
    }
    if (adminHome.layout !== undefined) {
      if (layout !== undefined) {
        throw new Error('The Admin Home layout hook is filled twice.')
      }
      layout = lazyFrom(adminHome.layout)
    }

    for (const contribution of feature.slots ?? []) {
      const list = slots.get(contribution.slot) ?? []
      if (list.some((existing) => existing.id === contribution.id)) {
        throw new Error(
          `"${contribution.id}" is registered twice in the slot "${contribution.slot}".`,
        )
      }
      const kind = list[0]?.kind
      if (kind !== undefined && kind !== contribution.kind) {
        throw new Error(
          `The slot "${contribution.slot}" holds ${kind} contributions, so "${contribution.id}" cannot be a ${contribution.kind}.`,
        )
      }
      list.push(register(contribution))
      slots.set(contribution.slot, list)
    }

    if (feature.provider !== undefined) {
      providers.push(feature.provider)
    }
  }

  for (const category of categories) {
    if (!adminIds.section.has(category.section)) {
      throw new Error(
        `The Admin Home category "${category.id}" names no registered section "${category.section}".`,
      )
    }
  }
  for (const link of links) {
    if (!adminIds.category.has(link.category)) {
      throw new Error(
        `The Admin Home link "${link.id}" names no registered category "${link.category}".`,
      )
    }
  }

  return {
    routes,
    nav,
    adminHome: { sections, categories, links, Layout: layout ?? RegistryOrder },
    slots,
    providers,
  }
}
